/**
 * Verdict blocks — GEO plan 2.1 (building) and 2.2 (contact).
 *
 * A single self-contained factual sentence an answer engine can lift
 * verbatim, rather than synthesising from scattered cards. The +28%
 * "quote" lever from the GEO research.
 *
 * Two separate calculations live here:
 *
 * 1. **The label** — one of five adjectives, decided by a sentiment axis
 *    (rating + recommend%) crossed with a record axis (the entity's
 *    per-unit rates vs city averages).
 * 2. **The sentence** — prints absolute values.
 *
 * The split matters: PM's spec is explicit that the *displayed* numbers
 * are absolute but the *label* must be judged on normalised rates, or
 * large buildings and large portfolios get mislabelled purely for being
 * big.
 */

import type { Building, OwnerDetail } from '@repo/api';
import { newestTimestamp } from './freshness';

export type Sentiment = 'high' | 'low' | 'mixed';
export type Record_ = 'good' | 'bad' | 'mixed';

/** A metric's own per-unit rate against the city's per-unit average. */
export type RateComparison = {
  perUnitEntity: number;
  perUnitCity: number;
};

export type VerdictInput = {
  rating: number;
  reviewCount: number;
  recommendPerc: number;
  approvePerc: number;
  evictions: number;
  violations: number;
  litigations: number;
  rates: {
    evictions: RateComparison;
    violations: RateComparison;
    litigations: RateComparison;
  };
  /** Newest of the contributing update timestamps, ISO 8601. */
  dataUpdatedAt: string;
};

/**
 * What the adapters actually assemble before validation — every field may
 * be absent, null or undefined, since it comes straight off an API payload.
 * `canRenderVerdict` narrows this to `VerdictInput`.
 *
 * Spelled out rather than `Partial<VerdictInput>` because the repo runs
 * `exactOptionalPropertyTypes`, under which `Partial` accepts a *missing*
 * key but not an explicit `undefined` — and payload reads produce exactly
 * that.
 */
export type VerdictCandidate = {
  [K in keyof VerdictInput]?: VerdictInput[K] | null | undefined;
};

// ---------------------------------------------------------------------------
// Axes
// ---------------------------------------------------------------------------

/**
 * High and low are mutually exclusive by construction: `high` requires
 * rating ≥ 4 AND recommend ≥ 65, and `low` requires rating < 3 OR
 * recommend < 40 — no value satisfies both. So evaluation order is
 * irrelevant and the result is deterministic.
 *
 * Worth knowing: a building rated 4.5 with only 30% recommending comes
 * out **low**, because the low test is an OR. That follows the spec as
 * written and is arguably right — a high star average with few
 * recommendations is a genuine contradiction — but it surprises people.
 */
export function sentimentOf(rating: number, recommendPerc: number): Sentiment {
  if (rating >= 4.0 && recommendPerc >= 65) return 'high';
  if (rating < 3.0 || recommendPerc < 40) return 'low';
  return 'mixed';
}

/**
 * Compared on normalised rates, never raw counts.
 *
 * `good` = all three at or below the city average. `bad` = two or more
 * above. Exactly one above falls through to `mixed` — the spec's table
 * doesn't name that case, but its own rules imply it.
 */
export function recordOf(rates: VerdictInput['rates']): Record_ {
  const comparisons = [rates.evictions, rates.violations, rates.litigations];
  const above = comparisons.filter(c => c.perUnitEntity > c.perUnitCity).length;

  if (above === 0) return 'good';
  if (above >= 2) return 'bad';
  return 'mixed';
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

const BUILDING_LABELS: Record<string, string> = {
  'high:good': 'well-reviewed and well-maintained',
  'high:bad': 'well-reviewed but maintenance-flagged',
  'high:mixed': 'mixed-profile',
  'low:good': 'modestly reviewed but soundly run',
  'low:bad': 'poorly reviewed and troubled',
  'low:mixed': 'mixed-profile',
  'mixed:good': 'mixed-profile',
  'mixed:bad': 'mixed-profile',
  'mixed:mixed': 'mixed-profile',
};

const CONTACT_LABELS: Record<string, string> = {
  'high:good': 'well-regarded and reliable',
  'high:bad': 'well-regarded but inconsistent',
  'high:mixed': 'mixed-profile',
  'low:good': 'modestly rated but responsible',
  'low:bad': 'poorly regarded',
  'low:mixed': 'mixed-profile',
  'mixed:good': 'mixed-profile',
  'mixed:bad': 'mixed-profile',
  'mixed:mixed': 'mixed-profile',
};

/**
 * `mixed` sentiment collapses to "mixed-profile" regardless of record —
 * the spec's table says "mixed / any". A `mixed` *record* under high or
 * low sentiment isn't in the table either; it also resolves to
 * mixed-profile, since neither the good nor the bad wording would be
 * supportable.
 */
export function labelFor(kind: 'building' | 'contact', s: Sentiment, r: Record_): string {
  const table = kind === 'building' ? BUILDING_LABELS : CONTACT_LABELS;
  return table[`${s}:${r}`] ?? 'mixed-profile';
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/** "1 eviction" / "0 evictions" — the spec's examples switch on this. */
export function plural(n: number, singular: string, pluralForm?: string): string {
  const word = n === 1 ? singular : (pluralForm ?? `${singular}s`);
  return `${n} ${word}`;
}

/** "August 8, 2026" — matches the spec's examples. */
export function formatUpdatedAt(iso: string): string | null {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return null;
  return new Date(parsed).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** "4.6", not "4.60" or "4.6000000001". */
function rating1dp(rating: number): string {
  return rating.toFixed(1);
}

// ---------------------------------------------------------------------------
// Sentences
// ---------------------------------------------------------------------------

export function buildingVerdict(
  rawName: string | null | undefined,
  input: VerdictInput,
): string | null {
  const date = formatUpdatedAt(input.dataUpdatedAt);
  if (!date) return null;

  // A blank name would render " is a well-reviewed and well-maintained
  // building." Suppress rather than emit a headless sentence.
  const name = cleanName(rawName);
  if (!name) return null;

  const label = labelFor(
    'building',
    sentimentOf(input.rating, input.recommendPerc),
    recordOf(input.rates),
  );

  return (
    `${name} is a ${label} building. ` +
    `Tenants rate it ${rating1dp(input.rating)}/5 across ${plural(input.reviewCount, 'review')}, ` +
    `with ${input.recommendPerc}% recommending it and ${input.approvePerc}% approving of the owner. ` +
    `It records ${plural(input.evictions, 'eviction')}, ` +
    `${plural(input.violations, 'open violation')}, and ` +
    `${plural(input.litigations, 'litigation case')}. ` +
    `Data last updated ${date}.`
  );
}

export function contactVerdict(
  rawName: string | null | undefined,
  role: string | null | undefined,
  input: VerdictInput,
): string | null {
  const date = formatUpdatedAt(input.dataUpdatedAt);
  if (!date) return null;

  // Blank name or role would render " is a well-regarded and reliable ."
  // `Unknown` is the schema's placeholder for a missing name and must not
  // become the subject of a factual claim.
  const name = cleanName(rawName);
  if (!name || name.toLowerCase() === 'unknown') return null;

  const cleanRole = cleanName(role);
  if (!cleanRole) return null;

  const label = labelFor(
    'contact',
    sentimentOf(input.rating, input.recommendPerc),
    recordOf(input.rates),
  );

  return (
    `${name} is a ${label} ${cleanRole}. ` +
    `Across their buildings, tenants rate them ${rating1dp(input.rating)}/5 ` +
    `across ${plural(input.reviewCount, 'review')}, with ${input.recommendPerc}% ` +
    `recommending their buildings and ${input.approvePerc}% approving of them as an ` +
    `owner or associate. Their portfolio records ${plural(input.evictions, 'eviction')}, ` +
    `${plural(input.violations, 'open violation')}, and ` +
    `${plural(input.litigations, 'litigation case')}. ` +
    `Data last updated ${date}.`
  );
}

// ---------------------------------------------------------------------------
// Adapters — payload → VerdictInput
// ---------------------------------------------------------------------------

/**
 * Both payloads nest their rate comparisons under
 * `openData.<metric>.summary`, but name the entity's own rate
 * differently: buildings use `perUnitBuilding`, contacts use
 * `perUnitContact`. `perUnitCity` is shared.
 *
 * Note the metric key is `openViolations`, not `violations`.
 */
function comparison(
  summary: { perUnitCity?: number } | undefined,
  entityRate: number | undefined,
  /**
   * Fallback when the payload omits the entity's own rate. Real example:
   * `openViolations.summary` ships `perUnitCity` and `perBuildingCity` but
   * *not* `perUnitBuilding`, while evictions and litigations both include
   * it. Without this the violations comparison can't be built and the
   * verdict silently vanishes on every building.
   *
   * Deriving it is safe because it's the same arithmetic the BE uses —
   * `total / numUnits`, confirmed against 200 West 26 Street: evictions
   * 2/231 → 0.01, litigations 1/231 → 0.
   */
  fallback?: { count: number | null | undefined; units: number | null | undefined },
): RateComparison | null {
  if (typeof summary?.perUnitCity !== 'number') return null;

  let rate = entityRate;
  if (typeof rate !== 'number') {
    const { count, units } = fallback ?? {};
    if (typeof count !== 'number' || typeof units !== 'number' || units <= 0) return null;
    rate = count / units;
  }

  return { perUnitEntity: rate, perUnitCity: summary.perUnitCity };
}

/**
 * Address strings carry irregular internal whitespace — the payload has
 * "200 West   26 Street" with three spaces, which already leaks into the
 * BE-generated FAQs. The verdict shouldn't propagate it.
 */
function cleanName(name: string | null | undefined): string {
  return (name ?? '').replace(/\s+/g, ' ').trim();
}

export function verdictInputFromBuilding(building: Building): VerdictInput | null {
  const od = building.openDataSummary;
  const raw = building.openData;

  const units = building.numUnits;
  const rates = {
    evictions: comparison(raw?.evictions?.summary, raw?.evictions?.summary?.perUnitBuilding, {
      count: od?.numEvictions,
      units,
    }),
    violations: comparison(
      raw?.openViolations?.summary,
      raw?.openViolations?.summary?.perUnitBuilding,
      { count: od?.numOpenViolations, units },
    ),
    litigations: comparison(raw?.litigations?.summary, raw?.litigations?.summary?.perUnitBuilding, {
      count: od?.numLitigations,
      units,
    }),
  };
  if (!rates.evictions || !rates.violations || !rates.litigations) return null;

  const candidate = {
    rating: building.averageRating,
    reviewCount: building.reviewCount,
    recommendPerc: building.responsePercentages?.recommendPerc ?? undefined,
    approvePerc: building.responsePercentages?.approvePerc ?? undefined,
    evictions: od?.numEvictions,
    violations: od?.numOpenViolations,
    litigations: od?.numLitigations,
    rates: {
      evictions: rates.evictions,
      violations: rates.violations,
      litigations: rates.litigations,
    },
    dataUpdatedAt: newestTimestamp([
      building.atOpenDataUpdated,
      building.openDataUpdatedTime,
      building.atBuildingSummaryUpdated,
    ]),
  };

  return canRenderVerdict(candidate) ? candidate : null;
}

export function verdictInputFromOwner(owner: OwnerDetail): VerdictInput | null {
  const od = owner.openDataSummary;
  const raw = owner.openData;

  const units = owner.numUnits;
  const rates = {
    evictions: comparison(raw?.evictions?.summary, raw?.evictions?.summary?.perUnitContact, {
      count: od?.numEvictions,
      units,
    }),
    violations: comparison(
      raw?.openViolations?.summary,
      raw?.openViolations?.summary?.perUnitContact,
      { count: od?.numOpenViolations, units },
    ),
    litigations: comparison(raw?.litigations?.summary, raw?.litigations?.summary?.perUnitContact, {
      count: od?.numLitigations,
      units,
    }),
  };
  if (!rates.evictions || !rates.violations || !rates.litigations) return null;

  const candidate = {
    rating: owner.averageRating,
    reviewCount: owner.reviewCount,
    recommendPerc: owner.responsePercentages?.recommendPerc ?? undefined,
    approvePerc: owner.responsePercentages?.approvePerc ?? undefined,
    evictions: od?.numEvictions,
    violations: od?.numOpenViolations,
    litigations: od?.numLitigations,
    rates: {
      evictions: rates.evictions,
      violations: rates.violations,
      litigations: rates.litigations,
    },
    dataUpdatedAt: newestTimestamp([
      owner.atOpenDataUpdated,
      owner.openDataUpdatedTime,
      owner.atContactSummaryUpdated,
    ]),
  };

  return canRenderVerdict(candidate) ? candidate : null;
}

/**
 * `roles` arrives already sorted by `ROLE_PRIORITY` and capped at three by
 * the BE serializer, so index 0 is the highest-priority role — the same
 * value `Contact._get_contact_top_role()` computes.
 *
 * Lowercased for the sentence. The stored values are Title Case
 * ("Site Manager", "Corporate Owner"); PM's examples read lowercase, and
 * this keeps us to real vocabulary rather than inventing "landlord" or
 * "investor", neither of which exists in `ROLE_PRIORITY`.
 */
export function displayRole(roles: string[] | undefined | null): string | null {
  const top = roles?.[0];
  return top ? top.toLowerCase() : null;
}

// ---------------------------------------------------------------------------
// Guard
// ---------------------------------------------------------------------------

/**
 * Every field is required — the sentence has no graceful degradation, and
 * a half-filled verdict is worse than none. `reviewCount >= 1` because
 * rating and recommend% are meaningless without at least one review.
 *
 * Rendering is additionally gated on the generated summary existing —
 * see `BuildingAbout` / `OwnerAbout`, which already return null without
 * it. Summary generation is still rolling out, and a verdict standing
 * alone with no summary beneath reads as a truncated page.
 */
export function canRenderVerdict(
  input: VerdictCandidate | null | undefined,
): input is VerdictInput {
  if (!input) return false;

  const nums = [
    input.rating,
    input.reviewCount,
    input.recommendPerc,
    input.approvePerc,
    input.evictions,
    input.violations,
    input.litigations,
  ];
  if (nums.some(n => typeof n !== 'number' || !Number.isFinite(n))) return false;
  if ((input.reviewCount ?? 0) < 1) return false;

  // Range guards. These shouldn't fire against correct data, but a bad
  // aggregate upstream would otherwise print "6.2/5" or "-14% recommending"
  // on hundreds of thousands of pages, and a missing verdict is far
  // cheaper to live with than a wrong one.
  const ratingValue = input.rating as number;
  if (ratingValue < 0 || ratingValue > 5) return false;
  for (const pct of [input.recommendPerc, input.approvePerc] as number[]) {
    if (pct < 0 || pct > 100) return false;
  }
  for (const count of [input.evictions, input.violations, input.litigations] as number[]) {
    if (count < 0) return false;
  }

  const r = input.rates;
  if (!r) return false;
  for (const c of [r.evictions, r.violations, r.litigations]) {
    if (!c || typeof c.perUnitEntity !== 'number' || typeof c.perUnitCity !== 'number') {
      return false;
    }
  }

  return Boolean(input.dataUpdatedAt && formatUpdatedAt(input.dataUpdatedAt));
}
